import { SPLIT_CHAR, WHITE_LIST_CHARS } from '@/data/constants';
import { isHebrew, isString } from './is';

/**
 * create random string
 * @param {number} length result string length
 * @returns {string} random string
 */
const generateRandomString = (length = 10) => (Array.from({ length }, () => WHITE_LIST_CHARS[Math.floor(Math.random() * WHITE_LIST_CHARS.length)]).join(''));

/**
 * replace string inside another string in specific position
 * @param {object} parameters
 * @returns {string}
 */
const replaceLetters = (parameters) => {
  const {
    string = '',
    from = 0,
    to = 0,
    replacement = '',
  } = parameters;

  return string.substring(0, from) + replacement + string.substring(to);
};

/**
 * add commas to number
 * @param {number} number
 * @returns {string} number with commas
 */
const addCommas = (number) => {
  const num = (typeof number === 'number') ? number : convertStringToNumber(number);
  return num.toLocaleString('en-US');
};

/**
 * limit number digits
 * @param {number} number
 * @param {number} digits
 * @returns {number}
 */
const limitNumberDigits = (number, digits = 3) => {
  const re = new RegExp(`^-?\\d+(?:.\\d{0,${digits || -1}})?`);
  return number.toString().match(re)[0];
};

/**
 * remove all special chars from string and leave only letters and numbers
 * @param {string} path
 * @returns {string}
 */
const getCleanString = (path = '') => String(path).replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

/**
 * add slash to path if not exists as first char
 * @param {string} path
 * @returns {string}
 */
const addSlashToPath = (path = '') => ((String(path).charAt(0) === '/') ? path : `/${path}`);

/**
 * convert string to number if can, returns back val if cant convert
 * @param {mixed} val
 * @returns {mixed}
 */
const convertStringToNumber = (val = '') => (
  (typeof val === 'string' && !Number.isNaN(parseInt(val, 10)))
    ? parseInt(val, 10)
    : val
);

/**
 * convert string to float if can, returns back val if cant convert
 * @param {mixed} val
 * @returns {mixed}
 */
const convertStringToFloat = (val = '') => (
  (typeof val === 'string' && !Number.isNaN(parseInt(val, 10)))
    ? parseFloat(val, 10)
    : val
);

/**
 * slugify text
 * @param {string} str
 * @returns {string}
 */
const slugify = (str = '') => String(str)
  .normalize('NFKD') // split accented characters into their base characters and diacritical marks
  .replace(/[\u0300-\u036f]/g, '') // remove all the accents, which happen to be all in the \u03xx UNICODE block.
  .trim() // trim leading or trailing whitespace
  .toLowerCase() // convert to lowercase
  .replace(/[^a-z0-9 -]/g, '') // remove non-alphanumeric characters
  .replace(/\s+/g, '-') // replace spaces with hyphens
  .replace(/-+/g, '-'); // remove consecutive hyphens

/**
 * truncate string and put dots in the middle (ellipsis)
 * @param {string} str source string
 * @param {number} firstCharCount number of characters to be shown in the first portion
 * @param {*} endCharCount number of characters to be shown in the end portion
 * @param {*} dotCount count of dots to be placed between the first and end portions
 * @param {string} replaceChar character to replace with
 * @returns {string}
 */
const truncateString = (str = '', firstCharCount = str.length, endCharCount = 0, dotCount = 3, replaceChar = '.') => {
  // No truncation needed
  if (str.length <= firstCharCount + endCharCount) {
    return str;
  }

  const firstPortion = str.slice(0, firstCharCount);
  const endPortion = str.slice(-endCharCount);
  const dots = replaceChar.repeat(dotCount);

  return `${firstPortion}${dots}${endPortion}`;
};

/**
 * get value from object using string dot notation. example: 'details.name.first'
 * @param {object} data
 * @param {string} str - dot notation string
 * @returns {mixed}
 */
const getItemValue = (data, str) => {
  let output = null;
  const source = String(str).trim();

  try {
    output = source.split(SPLIT_CHAR).reduce((a, b) => a[b], data);
  } catch (error) {
    // nothing to do here...
  }

  return output;
};

/**
 * Convert dot notation to nested object
 * @param {string} dotNotation
 * @param {mixed} value
 * @returns {object}
 */
const stringToNestedObject = (dotNotation, value) => {
  const keys = dotNotation.split('.');
  const result = {};
  const keyValue = (isString(value)) ? { value } : value;

  keys.reduce((acc, key, index) => {
    // If it's the last key, set the value
    if (index === keys.length - 1) {
      acc[key] = keyValue;
    } else {
      // Create a new nested object if it doesn't exist
      acc[key] = acc[key] || {};
    }

    return acc[key];
  }, result);

  return result;
};

const formatNumberWithCommas = (val) => {
  if (val === 0) {
    return '0';
  }
  if (!val) return '';

  // Ensure it's a string
  const numStr = typeof val === 'number' ? val.toString() : val;

  // Remove any existing commas
  const cleanedNumStr = numStr.replace(/,/g, '');

  // Split integer and decimal parts
  const [integerPart, decimalPart] = cleanedNumStr.split('.');

  // Format integer part with commas
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');

  // Return combined result
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

const handleNumericInput = (e, updateField, planId, rateIndex, fieldName, maxDecimalPlaces = 2) => {
  let { value } = e.target;
  value = value.replace(/[^0-9.]/g, '');
  const decimalCount = (value.match(/\./g) || []).length;
  if (decimalCount > 1) {
    value = value.substring(0, value.lastIndexOf('.')) + value.slice(value.lastIndexOf('.') + 1);
  }
  if (value.length > 3 && !value.includes('.')) {
    value = `${value.slice(0, 3)}.${value.slice(3)}`;
  }
  const regex = new RegExp(`^([0-9]{1,3}(?:\\.[0-9]{0,${maxDecimalPlaces}})?)`);
  value = (value.match(regex) || [])[0] || '';
  updateField(planId, rateIndex, { [fieldName]: value });
};

const rtlFix = (s) => {
  const str = String(s ?? '');
  return isHebrew(str) ? str.split('').reverse().join('') : str;
};

const arrayBufferToBase64 = (buffer) => {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

export {
  generateRandomString,
  replaceLetters,
  addCommas,
  getCleanString,
  addSlashToPath,
  convertStringToNumber,
  limitNumberDigits,
  convertStringToFloat,
  slugify,
  truncateString,
  getItemValue,
  stringToNestedObject,
  formatNumberWithCommas,
  handleNumericInput,
  rtlFix,
  arrayBufferToBase64,
};
